import * as React from "react"

import { cn } from "@/lib/utils"

export interface InputProps extends React.ComponentProps<"input"> {
  /**
   * Error message to display below input
   * Automatically sets aria-invalid and aria-describedby
   */
  error?: string
  /**
   * Helper text shown below input (when no error)
   */
  helperText?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, helperText, id, ...props }, ref) => {
    // Always call useId unconditionally (rules of hooks)
    const generatedId = React.useId()
    // Use provided id or fall back to generated one
    const inputId = id || generatedId
    const errorId = `${inputId}-error`
    const helperId = `${inputId}-helper`
    const hasError = Boolean(error)
    const hasHelper = Boolean(helperText) && !hasError

    // Build aria-describedby
    const describedBy = hasError ? errorId : hasHelper ? helperId : undefined

    const inputElement = (
      <input
          type={type}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={cn(
            "flex h-11 w-full rounded-xl border px-4 py-2 text-base text-foreground",
            // Background with dark mode support
            "bg-background/80",
            "file:border-0 file:bg-transparent file:text-base file:font-medium file:text-foreground",
            "placeholder:text-muted-foreground/70",
            // Liquid Glass Input — using CSS custom properties (SSOT)
            "border-border/60",
            "backdrop-blur-sm",
            "shadow-[var(--shadow-input)]",
            // Hover — elevate with glow
            "hover:border-primary/40 hover:bg-background/90",
            "hover:shadow-[var(--shadow-input-hover)]",
            // Focus — full Liquid Glass glow
            "focus-visible:outline-none",
            "focus-visible:border-primary/80",
            "focus-visible:bg-background",
            "focus-visible:shadow-[var(--shadow-input-focus)]",
            "focus-visible:ring-0",
            // Transitions — 240ms canonical
            "transition-all duration-240 ease-out",
            // States
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border/60 disabled:hover:shadow-none",
            // Error state
            hasError && "border-destructive shadow-[0_0_0_3px_hsl(0_70%_50%/0.15)]",
            // Size
            "sm:h-10 sm:text-sm",
            // Hide number input spinner
            type === "number" && "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]",
            className
          )}
        ref={ref}
        {...props}
      />
    )

    // Return plain input if no error/helper (backwards compatible)
    if (!hasError && !hasHelper) {
      return inputElement
    }

    // Wrap with error/helper text
    return (
      <div className="w-full">
        {inputElement}
        {/* Error message */}
        {hasError && (
          <p
            id={errorId}
            className="mt-1.5 text-sm text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
        {/* Helper text */}
        {hasHelper && (
          <p
            id={helperId}
            className="mt-1.5 text-sm text-muted-foreground"
          >
            {helperText}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
